import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Form, Button, Alert, Spinner, Badge, Modal, Tab, Tabs, Pagination } from 'react-bootstrap';

import axios from 'axios';

// Custom component to display images with authorization
const AuthorizedImage: React.FC<{
  src: string;
  alt: string;
  style?: React.CSSProperties;
  onError?: () => void;
}> = ({ src, alt, style, onError }) => {
  const [imageUrl, setImageUrl] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    const fetchImage = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(src, {
          headers: {
            Authorization: `Bearer ${token}`
          },
          responseType: 'blob'
        });
        
        const imageObjectUrl = URL.createObjectURL(response.data);
        setImageUrl(imageObjectUrl);
        setLoading(false);
      } catch (err) {
        console.error('Failed to load image:', err);
        setError(true);
        setLoading(false);
        if (onError) onError();
      }
    };

    fetchImage();

    // Cleanup function to revoke the object URL
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [src, onError, imageUrl]);

  if (loading) {
    return (
      <div style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  if (error || !imageUrl) {
    return (
      <div style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa' }}>
        <small className="text-muted">Failed to load image</small>
      </div>
    );
  }

  return <img src={imageUrl} alt={alt} style={style} />;
};

interface AdminProfileManagementProps {
  userId?: string;
  onClose?: () => void;
}

interface UserProfile {
  _id: string;
  name: string;
  email: string;
  profile: {
    phone: string;
    dateOfBirth: string;
    gender: string;
    address: {
      street: string;
      city: string;
      state: string;
      pincode: string;
      country: string;
    };
  };
  bankDetails: {
    accountNumber: string;
    ifscCode: string;
    bankName: string;
    accountHolderName: string;
    accountType: string;
    isVerified: boolean;
    status?: string;
    adminNotes?: string;
  };
  kycDetails: {
    panNumber: string;
    aadhaarNumber?: string;
    isVerified: boolean;
    status?: string;
    adminNotes?: string;
    documents?: Array<{
      type: string;
      filename: string;
      originalName: string;
      mimeType: string;
      size: number;
      uploadDate: string;
      isVerified: boolean;
      verifiedAt?: string;
      verifiedBy?: string;
      rejectionReason?: string;
    }>;
  };
  profileLocked: boolean;
  profileLockedAt?: string;
  profileLockedBy?: string;
  lastModifiedBy?: string;
  lastModifiedAt?: string;
  adminNotes?: Array<{
    note: string;
    addedBy: string;
    addedAt: string;
  }>;
}

const AdminProfileManagement: React.FC<AdminProfileManagementProps> = ({ userId: propUserId, onClose }) => {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('personal');
  const [adminNote, setAdminNote] = useState('');
  const [showLockModal, setShowLockModal] = useState(false);
  const [lockReason, setLockReason] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(propUserId || null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  const fetchUserProfile = React.useCallback(async () => {
    if (!selectedUserId) return;
    
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/admin/users/${selectedUserId}/profile`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        setUserProfile(response.data.user);
      } else {
        setError(response.data.message || 'Failed to load user profile');
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to load user profile');
    } finally {
      setLoading(false);
    }
  }, [selectedUserId, API_BASE_URL]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchUsers();
  };

  const selectUser = (userId: string) => {
    setSelectedUserId(userId);
  };

  const backToUsersList = () => {
    setSelectedUserId(null);
    setUserProfile(null);
    fetchUsers();
  };

  const fetchUsers = React.useCallback(async () => {
    try {
      setSearchLoading(true);
      const response = await axios.get(`${API_BASE_URL}/admin/users?page=${currentPage}&limit=${pageSize}&search=${encodeURIComponent(searchTerm)}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.data.success) {
        setUsers(response.data.users);
        setTotalUsers(response.data.total);
      }
    } catch (error: any) {
      setError('Failed to load users');
    } finally {
      setSearchLoading(false);
    }
  }, [API_BASE_URL, currentPage, searchTerm]);

  React.useEffect(() => {
    if (selectedUserId) {
      fetchUserProfile();
    } else {
      fetchUsers();
    }
  }, [fetchUserProfile, selectedUserId, fetchUsers]);

  // Handle pagination
  React.useEffect(() => {
    if (!selectedUserId) {
      fetchUsers();
    }
  }, [currentPage, fetchUsers, selectedUserId]);

  // Function to verify a document
  const verifyDocument = async (docFilename: string) => {
    if (!userProfile || !selectedUserId) return;

    try {
      setSaving(true);
      const response = await axios.post(`${API_BASE_URL}/admin/users/${selectedUserId}/verify-document`, {
        filename: docFilename
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        setMessage('Document verified successfully');
        // Refresh user profile to show updated status
        fetchUserProfile();
      } else {
        setError(response.data.message || 'Failed to verify document');
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to verify document');
    } finally {
      setSaving(false);
    }
  };

  // Function to reject a document
  const rejectDocument = async (docFilename: string, reason: string) => {
    if (!userProfile || !selectedUserId) return;

    try {
      setSaving(true);
      const response = await axios.post(`${API_BASE_URL}/admin/users/${selectedUserId}/reject-document`, {
        filename: docFilename,
        reason: reason
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        setMessage('Document rejected');
        // Refresh user profile to show updated status
        fetchUserProfile();
      } else {
        setError(response.data.message || 'Failed to reject document');
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to reject document');
    } finally {
      setSaving(false);
    }
  };



  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (!userProfile) return;

    if (name.includes('profile.address.')) {
      const field = name.split('.')[2];
      setUserProfile(prev => ({
        ...prev!,
        profile: {
          ...prev!.profile,
          address: {
            ...prev!.profile.address,
            [field]: value
          }
        }
      }));
    } else if (name.includes('profile.')) {
      const field = name.split('.')[1];
      setUserProfile(prev => ({
        ...prev!,
        profile: {
          ...prev!.profile,
          [field]: value
        }
      }));
    } else if (name.includes('bankDetails.')) {
      const field = name.split('.')[1];
      setUserProfile(prev => {
        const newBankDetails = {
          ...prev!.bankDetails,
          [field]: value
        };
        
        // If status is being changed, update isVerified accordingly
        if (field === 'status') {
          newBankDetails.isVerified = value === 'verified';
        }
        
        return {
          ...prev!,
          bankDetails: newBankDetails
        };
      });
    } else if (name.includes('kycDetails.')) {
      const field = name.split('.')[1];
      setUserProfile(prev => {
        const newKycDetails = {
          ...prev!.kycDetails,
          [field]: field === 'panNumber' || field === 'aadhaarNumber' ? value.toUpperCase() : value
        };
        
        // If status is being changed, update isVerified accordingly
        if (field === 'status') {
          newKycDetails.isVerified = value === 'verified';
        }
        
        return {
          ...prev!,
          kycDetails: newKycDetails
        };
      });
    } else {
      setUserProfile(prev => ({
        ...prev!,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userProfile) return;

    setError('');
    setMessage('');
    setSaving(true);

    try {
      const response = await axios.put(`${API_BASE_URL}/admin/users/${selectedUserId}/profile`, {
        name: userProfile.name,
        profile: userProfile.profile,
        bankDetails: userProfile.bankDetails,
        kycDetails: userProfile.kycDetails,
        adminNote: adminNote
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        setMessage('Profile updated successfully');
        setUserProfile(response.data.user);
        setAdminNote('');
      } else {
        setError(response.data.message || 'Failed to update profile');
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleLock = async () => {
    if (!userProfile) return;

    try {
      setSaving(true);
      const response = await axios.patch(`${API_BASE_URL}/admin/users/${selectedUserId}/profile/lock`, {
        locked: !userProfile.profileLocked,
        reason: lockReason
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        setMessage(`Profile ${userProfile.profileLocked ? 'unlocked' : 'locked'} successfully`);
        setUserProfile(response.data.user);
        setShowLockModal(false);
        setLockReason('');
      } else {
        setError(response.data.message || 'Failed to toggle lock status');
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to toggle lock status');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div>
        <Row className="mb-4">
          <Col>
            <h2>User Profile Management</h2>
            <p className="text-muted">Manage all user profiles, KYC details, and account settings</p>
          </Col>
        </Row>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </Alert>
        )}

        <Card>
          <Card.Header>
            <Row className="align-items-center">
              <Col md={8}>
                <h5 className="mb-0">All Users</h5>
              </Col>
              <Col md={4}>
                <div className="d-flex">
                  <Form.Control
                    type="text"
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="me-2"
                  />
                  <Button 
                    onClick={handleSearch} 
                    disabled={searchLoading}
                    variant="outline-primary"
                  >
                    <i className="fas fa-search"></i>
                  </Button>
                </div>
              </Col>
            </Row>
          </Card.Header>
          <Card.Body className="p-0">
            {searchLoading ? (
              <div className="text-center p-4">
                <Spinner animation="border" />
                <p className="mt-2 mb-0">Loading users...</p>
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>User</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Profile Status</th>
                        <th>KYC Status</th>
                        <th>Joined</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.slice((currentPage - 1) * pageSize, (currentPage - 1) * pageSize + pageSize).map((user) => (
                        <tr key={user._id}>
                          <td>
                            <div>
                              <strong>{user.name}</strong>
                              <br />
                              <small className="text-muted">
                                {user.profile?.phone || 'No phone'}
                              </small>
                            </div>
                          </td>
                          <td>{user.email}</td>
                          <td>
                            <Badge bg={user.role === 'admin' ? 'primary' : 'secondary'}>
                              {user.role}
                            </Badge>
                          </td>
                          <td>
                            <Badge bg={user.profileLocked ? 'danger' : 'success'}>
                              {user.profileLocked ? 'Locked' : 'Active'}
                            </Badge>
                          </td>
                          <td>
                            <Badge bg={user.kycDetails?.isVerified ? 'success' : 'warning'}>
                              {user.kycDetails?.isVerified ? 'Verified' : 'Pending'}
                            </Badge>
                          </td>
                          <td>
                            <small>
                              {new Date(user.createdAt).toLocaleDateString()}
                            </small>
                          </td>
                          <td>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => selectUser(user._id)}
                            >
                              <i className="fas fa-edit me-1"></i>
                              Manage
                            </Button>
                          </td>
                        </tr>
                      ))}
                                    {/* Pagination Controls (below table) */}
                                    <div className="d-flex justify-content-between align-items-center mt-3 mb-2">
                                      <div>
                                        <Form.Select
                                          style={{ width: 120, display: 'inline-block' }}
                                          value={pageSize}
                                          onChange={e => {
                                            setPageSize(Number(e.target.value));
                                            setCurrentPage(1);
                                          }}
                                          size="sm"
                                        >
                                          {[5, 10, 20, 50, 100].map(size => (
                                            <option key={size} value={size}>{size} / page</option>
                                          ))}
                                        </Form.Select>
                                      </div>
                                      <div>
                                        <Pagination className="mb-0">
                                          <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                                          <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} />
                                          {(() => {
                                            const totalPages = Math.ceil(users.length / pageSize);
                                            let start = Math.max(1, currentPage - 2);
                                            let end = Math.min(totalPages, currentPage + 2);
                                            if (end - start < 4) {
                                              if (start === 1) end = Math.min(totalPages, start + 4);
                                              if (end === totalPages) start = Math.max(1, end - 4);
                                            }
                                            return Array.from({ length: end - start + 1 }, (_, i) => start + i).map(page => (
                                              <Pagination.Item
                                                key={page}
                                                active={page === currentPage}
                                                onClick={() => setCurrentPage(page)}
                                              >
                                                {page}
                                              </Pagination.Item>
                                            ));
                                          })()}
                                          <Pagination.Next onClick={() => setCurrentPage(p => Math.min(Math.ceil(users.length / pageSize), p + 1))} disabled={currentPage === Math.ceil(users.length / pageSize) || users.length === 0} />
                                          <Pagination.Last onClick={() => setCurrentPage(Math.ceil(users.length / pageSize))} disabled={currentPage === Math.ceil(users.length / pageSize) || users.length === 0} />
                                        </Pagination>
                                      </div>
                                    </div>
                    </tbody>
                  </table>
                </div>
                
                {users.length === 0 && !searchLoading && (
                  <div className="text-center p-4">
                    <i className="fas fa-users fa-3x text-muted mb-3"></i>
                    <p className="text-muted mb-0">No users found</p>
                  </div>
                )}
                
                {totalUsers > pageSize && (
                  <div className="p-3 border-top">
                    <div className="d-flex justify-content-between align-items-center">
                      <small className="text-muted">
                        Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalUsers)} of {totalUsers} users
                      </small>
                      <div>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          disabled={currentPage === 1}
                          onClick={() => setCurrentPage(prev => prev - 1)}
                          className="me-2"
                        >
                          Previous
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          disabled={currentPage * pageSize >= totalUsers}
                          onClick={() => setCurrentPage(prev => prev + 1)}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <Button variant="outline-secondary" onClick={backToUsersList} className="mb-2">
                <i className="fas fa-arrow-left me-2"></i>
                Back to Users List
              </Button>
              <h2>Manage User Profile</h2>
              <p className="text-muted">
                {userProfile.name} ({userProfile.email})
              </p>
            </div>
            <div className="d-flex gap-2">
              <Button
                variant={userProfile.profileLocked ? "success" : "warning"}
                onClick={() => setShowLockModal(true)}
                disabled={saving}
              >
                <i className={`fas ${userProfile.profileLocked ? 'fa-unlock' : 'fa-lock'} me-2`}></i>
                {userProfile.profileLocked ? 'Unlock' : 'Lock'} Profile
              </Button>
              {onClose && (
                <Button variant="secondary" onClick={onClose}>
                  Close
                </Button>
              )}
            </div>
          </div>
        </Col>
      </Row>

      {userProfile.profileLocked && (
        <Row className="mb-4">
          <Col>
            <Alert variant="warning">
              <i className="fas fa-lock me-2"></i>
              <strong>Profile Locked</strong> - Locked on{' '}
              {userProfile.profileLockedAt ? new Date(userProfile.profileLockedAt).toLocaleDateString() : 'Unknown date'}
            </Alert>
          </Col>
        </Row>
      )}

      {(message || error) && (
        <Row className="mb-4">
          <Col>
            {message && <Alert variant="success">{message}</Alert>}
            {error && <Alert variant="danger">{error}</Alert>}
          </Col>
        </Row>
      )}

      <Form onSubmit={handleSubmit}>
        <Tabs activeKey={activeTab} onSelect={(key) => setActiveTab(key || 'personal')} className="mb-4">
          <Tab eventKey="personal" title="Personal Information">
            <Card>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Full Name *</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={userProfile.name}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Phone Number *</Form.Label>
                      <Form.Control
                        type="tel"
                        name="profile.phone"
                        value={userProfile.profile?.phone || ''}
                        onChange={handleInputChange}
                        placeholder="10-digit mobile number"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Date of Birth</Form.Label>
                      <Form.Control
                        type="date"
                        name="profile.dateOfBirth"
                        value={userProfile.profile?.dateOfBirth ? userProfile.profile.dateOfBirth.split('T')[0] : ''}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Gender</Form.Label>
                      <Form.Select
                        name="profile.gender"
                        value={userProfile.profile?.gender || ''}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <h5 className="mb-3">Address Information</h5>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Street Address *</Form.Label>
                      <Form.Control
                        type="text"
                        name="profile.address.street"
                        value={userProfile.profile?.address?.street || ''}
                        onChange={handleInputChange}
                        placeholder="House no., Street name"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>City *</Form.Label>
                      <Form.Control
                        type="text"
                        name="profile.address.city"
                        value={userProfile.profile?.address?.city || ''}
                        onChange={handleInputChange}
                        placeholder="City"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>State *</Form.Label>
                      <Form.Control
                        type="text"
                        name="profile.address.state"
                        value={userProfile.profile?.address?.state || ''}
                        onChange={handleInputChange}
                        placeholder="State"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Pincode *</Form.Label>
                      <Form.Control
                        type="text"
                        name="profile.address.pincode"
                        value={userProfile.profile?.address?.pincode || ''}
                        onChange={handleInputChange}
                        placeholder="6-digit pincode"
                        maxLength={6}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="bank" title="Bank Details">
            <Card>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Account Number *</Form.Label>
                      <Form.Control
                        type="text"
                        name="bankDetails.accountNumber"
                        value={userProfile.bankDetails?.accountNumber || ''}
                        onChange={handleInputChange}
                        placeholder="Bank account number"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>IFSC Code *</Form.Label>
                      <Form.Control
                        type="text"
                        name="bankDetails.ifscCode"
                        value={userProfile.bankDetails?.ifscCode || ''}
                        onChange={handleInputChange}
                        placeholder="IFSC Code"
                        style={{ textTransform: 'uppercase' }}
                        maxLength={11}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Bank Name *</Form.Label>
                      <Form.Control
                        type="text"
                        name="bankDetails.bankName"
                        value={userProfile.bankDetails?.bankName || ''}
                        onChange={handleInputChange}
                        placeholder="Name of the bank"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Account Holder Name *</Form.Label>
                      <Form.Control
                        type="text"
                        name="bankDetails.accountHolderName"
                        value={userProfile.bankDetails?.accountHolderName || ''}
                        onChange={handleInputChange}
                        placeholder="Name as per bank records"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Account Type</Form.Label>
                      <Form.Select
                        name="bankDetails.accountType"
                        value={userProfile.bankDetails?.accountType || ''}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Account Type</option>
                        <option value="savings">Savings</option>
                        <option value="current">Current</option>
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Check 
                        type="checkbox"
                        label="Mark Bank Details as Verified"
                        checked={userProfile.bankDetails?.isVerified || false}
                        onChange={(e) => setUserProfile(prev => ({
                          ...prev!,
                          bankDetails: {
                            ...prev!.bankDetails,
                            isVerified: e.target.checked,
                            status: e.target.checked ? 'verified' : 'not_verified'
                          }
                        }))}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Verification Status</Form.Label>
                      <Form.Select
                        name="bankDetails.status"
                        value={userProfile.bankDetails?.status || 'not_verified'}
                        onChange={handleInputChange}
                      >
                        <option value="not_verified">Not Verified</option>
                        <option value="pending">Pending Verification</option>
                        <option value="verified">Verified</option>
                        <option value="rejected">Rejected</option>
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Bank Verification Notes (Admin Only)</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        name="bankDetails.adminNotes"
                        value={userProfile.bankDetails?.adminNotes || ''}
                        onChange={handleInputChange}
                        placeholder="Add notes about bank verification..."
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <Form.Label>Verification Status</Form.Label>
                      <div>
                        <Badge bg={userProfile.bankDetails?.isVerified ? 'success' : 'warning'}>
                          {userProfile.bankDetails?.isVerified ? 'Verified' : 'Pending Verification'}
                        </Badge>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="kyc" title="KYC Details">
            <Card>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>PAN Number *</Form.Label>
                      <Form.Control
                        type="text"
                        name="kycDetails.panNumber"
                        value={userProfile.kycDetails?.panNumber || ''}
                        onChange={handleInputChange}
                        placeholder="ABCDE1234F"
                        pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}"
                        style={{ textTransform: 'uppercase' }}
                        maxLength={10}
                      />
                      <Form.Text className="text-muted">
                        Format: 5 letters, 4 numbers, 1 letter (e.g., ABCDE1234F)
                      </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Aadhaar Number</Form.Label>
                      <Form.Control
                        type="text"
                        name="kycDetails.aadhaarNumber"
                        value={userProfile.kycDetails?.aadhaarNumber || ''}
                        onChange={handleInputChange}
                        placeholder="1234 5678 9012"
                        maxLength={14}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Check 
                        type="checkbox"
                        label="Mark KYC as Verified"
                        checked={userProfile.kycDetails?.isVerified || false}
                        onChange={(e) => setUserProfile(prev => ({
                          ...prev!,
                          kycDetails: {
                            ...prev!.kycDetails,
                            isVerified: e.target.checked,
                            status: e.target.checked ? 'verified' : 'not_started'
                          }
                        }))}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>KYC Status</Form.Label>
                      <Form.Select
                        name="kycDetails.status"
                        value={userProfile.kycDetails?.status || 'not_started'}
                        onChange={handleInputChange}
                      >
                        <option value="not_started">Not Started</option>
                        <option value="documents_uploaded">Documents Uploaded</option>
                        <option value="under_review">Under Review</option>
                        <option value="verified">Verified</option>
                        <option value="rejected">Rejected</option>
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>KYC Notes (Admin Only)</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="kycDetails.adminNotes"
                        value={userProfile.kycDetails?.adminNotes || ''}
                        onChange={handleInputChange}
                        placeholder="Add notes about KYC verification..."
                      />
                    </Form.Group>

                    <div className="mb-3">
                      <Form.Label>Current Status</Form.Label>
                      <div>
                        <Badge bg={userProfile.kycDetails?.isVerified ? 'success' : 'warning'}>
                          {userProfile.kycDetails?.isVerified ? 'Verified' : 'Pending Verification'}
                        </Badge>
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* Documents Section */}
                {userProfile.kycDetails?.documents && userProfile.kycDetails.documents.length > 0 && (
                  <Row className="mt-4">
                    <Col>
                      <h5>Uploaded Documents</h5>
                      <div className="row">
                        {userProfile.kycDetails.documents.map((doc, index) => (
                          <div key={index} className="col-md-6 mb-3">
                            <Card className="h-100">
                              <Card.Body>
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                  <div>
                                    <Badge bg="info" className="mb-1">
                                      {doc.type.replace('_', ' ').toUpperCase()}
                                    </Badge>
                                    <p className="mb-1 small text-muted">
                                      {doc.originalName}
                                    </p>
                                    <p className="mb-1 small text-muted">
                                      Uploaded: {new Date(doc.uploadDate).toLocaleDateString()}
                                    </p>
                                  </div>
                                  <Badge bg={doc.isVerified ? 'success' : 'warning'}>
                                    {doc.isVerified ? 'Verified' : 'Pending'}
                                  </Badge>
                                </div>
                                
                                {/* Display image if it's an image file */}
                                {doc.mimeType.startsWith('image/') && (
                                  <div className="mb-2">
                                    <AuthorizedImage
                                      src={`${API_BASE_URL}/admin/users/${userProfile._id}/documents/${doc.filename}`}
                                      alt={`${doc.type} document`}
                                      style={{ 
                                        maxWidth: '100%', 
                                        maxHeight: '200px', 
                                        objectFit: 'contain',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '4px'
                                      }}
                                      onError={() => {
                                        console.log('Failed to load document image:', doc.filename);
                                      }}
                                    />
                                  </div>
                                )}
                                
                                <div className="d-flex justify-content-between align-items-center">
                                  <small className="text-muted">
                                    Size: {(doc.size / 1024).toFixed(1)} KB
                                  </small>
                                  <div>
                                    <Button
                                      size="sm"
                                      variant="outline-primary"
                                      className="me-2"
                                      onClick={() => {
                                        window.open(`${API_BASE_URL}/admin/users/${userProfile._id}/documents/${doc.filename}`, '_blank');
                                      }}
                                    >
                                      <i className="fas fa-eye"></i> View
                                    </Button>
                                    {!doc.isVerified && (
                                      <>
                                        <Button
                                          size="sm"
                                          variant="success"
                                          className="me-2"
                                          disabled={saving}
                                          onClick={() => {
                                            verifyDocument(doc.filename);
                                          }}
                                        >
                                          <i className="fas fa-check"></i> Verify
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="danger"
                                          disabled={saving}
                                          onClick={() => {
                                            const reason = prompt('Please enter rejection reason:');
                                            if (reason && reason.trim()) {
                                              rejectDocument(doc.filename, reason.trim());
                                            }
                                          }}
                                        >
                                          <i className="fas fa-times"></i> Reject
                                        </Button>
                                      </>
                                    )}
                                    {doc.isVerified && (
                                      <Badge bg="success" className="ms-2">
                                        <i className="fas fa-check-circle"></i> Verified
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                                
                                {doc.rejectionReason && (
                                  <Alert variant="danger" className="mt-2 mb-0">
                                    <small>
                                      <strong>Rejection Reason:</strong> {doc.rejectionReason}
                                    </small>
                                  </Alert>
                                )}
                              </Card.Body>
                            </Card>
                          </div>
                        ))}
                      </div>
                    </Col>
                  </Row>
                )}
                
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="notes" title="Admin Notes">
            <Card>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Add Admin Note</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={adminNote}
                    onChange={(e) => setAdminNote(e.target.value)}
                    placeholder="Add a note about this profile change..."
                  />
                </Form.Group>

                {userProfile.adminNotes && userProfile.adminNotes.length > 0 && (
                  <div>
                    <h6>Previous Notes</h6>
                    {userProfile.adminNotes.map((note, index) => (
                      <div key={index} className="border-start border-3 border-info ps-3 mb-3">
                        <p className="mb-1">{note.note}</p>
                        <small className="text-muted">
                          Added on {new Date(note.addedAt).toLocaleDateString()}
                        </small>
                      </div>
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>

        <Row>
          <Col>
            <div className="d-flex justify-content-between">
              <Button
                type="submit"
                variant="primary"
                disabled={saving}
                className="px-4"
              >
                {saving ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </Col>
        </Row>
      </Form>

      {/* Lock/Unlock Modal */}
      <Modal show={showLockModal} onHide={() => setShowLockModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {userProfile.profileLocked ? 'Unlock' : 'Lock'} Profile
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Reason for {userProfile.profileLocked ? 'unlocking' : 'locking'}</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={lockReason}
              onChange={(e) => setLockReason(e.target.value)}
              placeholder="Enter reason for this action..."
              required
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowLockModal(false)}>
            Cancel
          </Button>
          <Button 
            variant={userProfile.profileLocked ? "success" : "warning"}
            onClick={handleToggleLock}
            disabled={saving || !lockReason.trim()}
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Processing...
              </>
            ) : (
              <>
                <i className={`fas ${userProfile.profileLocked ? 'fa-unlock' : 'fa-lock'} me-2`}></i>
                {userProfile.profileLocked ? 'Unlock' : 'Lock'} Profile
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminProfileManagement;